import { SHIPPED_BOT_AVATAR_CENTER } from "./bot-avatar-shapes.js";

/** White pills in the robot visor. Same geometry on web, desktop, and native. */
export const CANONICAL_ROBOT_EYE_COLOR = "#FFFFFF";
export const CANONICAL_ROBOT_VISOR_BORDER_PX = 1;

/** Dark ink eyes on an organic body. Independent of the page background token. */
export const CANONICAL_ORGANIC_EYE_COLOR = "#0B0B0C";
export const CANONICAL_ORGANIC_EYES = {
  width: 7,
  height: 24,
  rx: 3.5,
  left: { x: -14, y: -12 },
  right: { x: 7, y: -12 },
} as const;

/** Ellipse pair drawn on a shipped shape, in the shared avatar viewBox. */
export const CANONICAL_SHAPE_EYES = {
  offsetX: 29,
  offsetY: -8,
  rx: 10,
  ry: 7,
} as const;

export function canonicalShapeEyeCenters(center = SHIPPED_BOT_AVATAR_CENTER) {
  return {
    cy: center + CANONICAL_SHAPE_EYES.offsetY,
    leftCx: center - CANONICAL_SHAPE_EYES.offsetX,
    rightCx: center + CANONICAL_SHAPE_EYES.offsetX,
    rx: CANONICAL_SHAPE_EYES.rx,
    ry: CANONICAL_SHAPE_EYES.ry,
  };
}

export type CanonicalRobotFace = {
  visorW: number;
  visorH: number;
  visorBorder: number;
  eyeW: number;
  eyeH: number;
  eyeGap: number;
  eyeRadius: number;
  eyeColor: string;
};

/**
 * Robot eyes for settings presets, the large preview, chat, and lists.
 * The visor border is included so the eyes stay inside it at every size.
 */
export function canonicalRobotFace(size: number): CanonicalRobotFace {
  const border = CANONICAL_ROBOT_VISOR_BORDER_PX;
  const visorW = Math.round(size * 0.68);
  const visorH = Math.round(size * 0.44);
  const visorInnerW = Math.max(0, visorW - border * 2);
  const visorInnerH = Math.max(0, visorH - border * 2);
  const preferredEyeW = Math.max(4, Math.round(size * 0.14));
  const preferredEyeH = Math.max(7, Math.round(size * 0.22));
  const preferredEyeGap = Math.max(3, Math.round(size * 0.1));
  const preferredSpan = preferredEyeW * 2 + preferredEyeGap;
  const scale = Math.min(1, visorInnerW / preferredSpan, visorInnerH / preferredEyeH);
  const eyeW = Math.floor(preferredEyeW * scale);
  const eyeH = Math.floor(preferredEyeH * scale);
  const eyeGap = Math.floor(preferredEyeGap * scale);
  const eyeRadius = Math.max(2, Math.round(eyeW * 0.5));
  return {
    visorW,
    visorH,
    visorBorder: border,
    eyeW,
    eyeH,
    eyeGap,
    eyeRadius,
    eyeColor: CANONICAL_ROBOT_EYE_COLOR,
  };
}
